import { useState, useCallback, useRef } from 'react';

interface UseColumnResizeProps {
    externalWidths?: Record<string, number>;
    onColumnResize?: (columnName: string, width: number) => void;
}

export function useColumnResize({ externalWidths, onColumnResize }: UseColumnResizeProps = {}) {
    const [internalWidths, setInternalWidths] = useState<Record<string, number>>({});
    const [isResizing, setIsResizing] = useState(false);
    const [spannerPosition, setSpannerPosition] = useState<number | null>(null);
    const resizingRef = useRef<{ columnName: string; startX: number; startWidth: number } | null>(null);

    const customWidths = externalWidths ?? internalWidths;

    const getColumnWidth = useCallback(
        (columnName: string, defaultWidth: number): number => {
            return customWidths[columnName] ?? defaultWidth;
        },
        [customWidths],
    );

    const startResize = useCallback((columnName: string, startX: number, startWidth: number) => {
        resizingRef.current = { columnName, startX, startWidth };
        setIsResizing(true);
        setSpannerPosition(startX);
    }, []);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!resizingRef.current) return;

            const { columnName, startX, startWidth } = resizingRef.current;
            const deltaX = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + deltaX);

            setSpannerPosition(e.clientX);

            if (onColumnResize) {
                onColumnResize(columnName, newWidth);
            } else {
                setInternalWidths((prev) => ({
                    ...prev,
                    [columnName]: newWidth,
                }));
            }
        },
        [onColumnResize],
    );

    const stopResize = useCallback(() => {
        resizingRef.current = null;
        setIsResizing(false);
        setSpannerPosition(null);
    }, []);

    return {
        customWidths,
        getColumnWidth,
        startResize,
        handleMouseMove,
        stopResize,
        isResizing,
        spannerPosition,
    };
}
